import { capabilitiesForRecording, type DataCoverage, type RaceEvent, type RaceRecording, type ReplayManifest } from './race';
import { normalizeRecording } from './replay';

export interface BufferedRange { startMs: number; endMs: number }

export function chunkIndex(manifest: ReplayManifest, atMs: number): number {
  const position = Math.max(0, Math.min(Math.max(0, manifest.durationMs - 1), Number.isFinite(atMs) ? atMs : 0));
  return Math.floor(position / manifest.chunkDurationMs);
}

export function hasCoverage(ranges: BufferedRange[], atMs: number, durationMs: number): boolean {
  return ranges.some(range => atMs >= range.startMs && (atMs < range.endMs || atMs === durationMs && range.endMs === durationMs));
}

/** Keep at most three windows, prioritizing the current cursor over late prefetch responses. */
export function retainChunks(chunks: Map<number, RaceRecording>, cursor: number): Map<number, RaceRecording> {
  return new Map([...chunks].sort(([a], [b]) => Math.abs(a - cursor) - Math.abs(b - cursor) || b - a).slice(0, 3));
}

export function bufferedRanges(manifest: ReplayManifest, chunks: Map<number, RaceRecording>): BufferedRange[] {
  return mergeRanges([...chunks].sort(([a], [b]) => a - b).map(([index, recording]) => ({ startMs: index * manifest.chunkDurationMs, endMs: Math.min(manifest.durationMs, index * manifest.chunkDurationMs + recording.durationMs) })));
}

function mergeRanges(ranges: BufferedRange[]): BufferedRange[] {
  const ordered = ranges.filter(range => range.endMs > range.startMs).sort((a, b) => a.startMs - b.startMs);
  const merged: BufferedRange[] = [];
  for (const range of ordered) {
    const previous = merged.at(-1);
    if (previous && range.startMs <= previous.endMs) previous.endMs = Math.max(previous.endMs, range.endMs);
    else merged.push({ ...range });
  }
  return merged;
}

function coverageForReplay(manifest: ReplayManifest, chunks: Map<number, RaceRecording>): DataCoverage | undefined {
  const ranges = bufferedRanges(manifest, chunks);
  if (!ranges.length) return undefined;
  const gaps: DataCoverage['gaps'] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.startMs > cursor) gaps.push({ fromMs: cursor, toMs: range.startMs, reason: '该段回放尚未载入' });
    cursor = Math.max(cursor, range.endMs);
  }
  if (cursor < manifest.durationMs) gaps.push({ fromMs: cursor, toMs: manifest.durationMs, reason: '该段回放尚未载入' });
  // Preserve source-declared gaps from each loaded window, translated to the session clock.
  for (const [index, recording] of chunks) for (const gap of recording.coverage?.gaps ?? []) {
    const offset = index * manifest.chunkDurationMs;
    const fromMs = Math.max(0, offset + gap.fromMs), toMs = Math.min(manifest.durationMs, offset + gap.toMs);
    if (toMs > fromMs) gaps.push({ fromMs, toMs, reason: gap.reason });
  }
  const first = ranges[0], last = ranges.at(-1)!;
  return { startMs: first.startMs, endMs: last.endMs, missingStart: first.startMs > 0 || Boolean([...chunks.values()].find(recording => recording.coverage?.missingStart)),
    gaps: [...new Map(gaps.map(gap => [`${gap.fromMs}:${gap.toMs}:${gap.reason}`, gap])).values()].sort((a, b) => a.fromMs - b.fromMs),
    note: '按已加载回放窗口绘制；未载入区间保留最后已知状态，等待补载。' };
}

/** Each window includes its own initial state. Namespace seed identities, but retain message identities. */
export function assembleSession(manifest: ReplayManifest, chunks: Map<number, RaceRecording>): RaceRecording {
  const ordered = [...chunks].sort(([a], [b]) => a - b);
  const drivers = new Map(ordered.flatMap(([, recording]) => recording.drivers.map(driver => [driver.number, driver] as const)));
  const events: RaceEvent[] = ordered.flatMap(([index, recording]) => {
    const offset = index * manifest.chunkDurationMs;
    return recording.events.map(event => {
      const base = { ...event, id: `${index}:${event.id}`, atMs: offset + event.atMs };
      if (event.type === 'message') return { ...base, type: 'message' as const, message: { ...event.message, atMs: offset + event.message.atMs } };
      if (event.type === 'pit') return { ...base, type: 'pit' as const, stop: { ...event.stop, atMs: offset + event.stop.atMs } };
      if (event.type === 'timing' && event.patch.recentLaps) return { ...base, type: 'timing' as const, driverNumber: event.driverNumber,
        patch: { ...event.patch, recentLaps: event.patch.recentLaps.map(lap => ({ ...lap, atMs: offset + lap.atMs })) } };
      return base as RaceEvent;
    });
  });
  return normalizeRecording({ schemaVersion: 1, session: manifest.session, durationMs: manifest.durationMs,
    drivers: [...drivers.values()], track: ordered.find(([, recording]) => recording.track.length > 2)?.[1].track ?? [], events,
    notice: 'F1Live 历史回放 · 全场时间轴，按需加载；历史数据可能包含上游事后修订。', coverage: coverageForReplay(manifest, chunks) });
}
